import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PaidLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    const fetchPaid = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/paid-leads`);
        const data = await res.json();
        if (data.error) setError(data.error);
        else setLeads(data.leads || []);
      } catch (e) { setError('Failed to load: ' + e.message); }
      setLoading(false);
    };
    fetchPaid();
  }, []);

  const formatDate = (iso) => iso ? new Date(iso).toLocaleString('hi-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';
  const formatDateOnly = (iso) => iso ? new Date(iso).toLocaleDateString('hi-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  const filtered = useMemo(() => {
    let r = leads;
    if (search.trim()) {
      const q = search.toLowerCase();
      r = r.filter(l => (l.name || '').toLowerCase().includes(q) || (l.mobile1 || '').includes(q) || (l.delivery_pincode || '').includes(q) || (l.delivery_city || '').toLowerCase().includes(q));
    }
    if (dateFrom) {
      const from = new Date(dateFrom).getTime();
      r = r.filter(l => l.paid_at && new Date(l.paid_at).getTime() >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo).getTime() + 86400000; // include the full day
      r = r.filter(l => l.paid_at && new Date(l.paid_at).getTime() < to);
    }
    return r;
  }, [leads, search, dateFrom, dateTo]);

  // Group by date for display
  const groupedByDate = useMemo(() => {
    const map = {};
    filtered.forEach(l => {
      const dateKey = l.paid_at ? new Date(l.paid_at).toLocaleDateString('hi-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'अज्ञात';
      if (!map[dateKey]) map[dateKey] = [];
      map[dateKey].push(l);
    });
    return Object.entries(map).sort((a, b) => {
      const da = a[1][0]?.paid_at ? new Date(a[1][0].paid_at).getTime() : 0;
      const db = b[1][0]?.paid_at ? new Date(b[1][0].paid_at).getTime() : 0;
      return db - da;
    });
  }, [filtered]);

  const totalRevenue = useMemo(() => filtered.reduce((s, l) => s + (l.payment_amount || 0), 0), [filtered]);

  const exportCSV = () => {
    const headers = ['Date', 'Name', 'Mobile', 'Email', 'Amount', 'Address', 'City', 'State', 'Pincode', 'Delivery Mobile', 'Payment ID'];
    const rows = filtered.map(l => [
      formatDate(l.paid_at),
      l.delivery_full_name || l.name || '',
      l.delivery_mobile || l.mobile1 || '',
      l.email || '',
      l.payment_amount || '',
      (l.delivery_address || '').replace(/,/g, ' '),
      l.delivery_city || '',
      l.delivery_state || '',
      l.delivery_pincode || '',
      l.delivery_mobile || '',
      l.razorpay_payment_id || '',
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `paid-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const printShippingLabel = (lead) => {
    const html = `<!DOCTYPE html><html><head><title>Shipping Label</title><meta charset="utf-8">
<style>
  body { font-family: 'Noto Sans Devanagari', sans-serif; padding: 20px; }
  .label { width: 4in; min-height: 6in; padding: 20px; border: 2px solid #000; box-sizing: border-box; }
  h2 { margin: 0 0 8px; font-size: 14px; color: #5B1818; text-transform: uppercase; letter-spacing: 1px; }
  .from { font-size: 11px; padding-bottom: 12px; border-bottom: 1px dashed #999; margin-bottom: 16px; }
  .to-label { font-size: 11px; color: #666; margin: 0 0 4px; }
  .name { font-size: 18px; font-weight: bold; margin: 0 0 6px; }
  .addr { font-size: 14px; line-height: 1.5; margin: 0 0 12px; }
  .pin { font-size: 22px; font-weight: bold; letter-spacing: 2px; padding: 10px; background: #f4ead2; text-align: center; margin: 12px 0; }
  .mobile { font-size: 14px; margin: 6px 0; }
  .meta { font-size: 10px; color: #888; margin-top: 16px; padding-top: 8px; border-top: 1px dashed #999; }
  @media print { body { padding: 0; } }
</style></head><body>
<div class="label">
  <h2>FROM</h2>
  <div class="from"><b>माता मलिहा सेवा</b><br/>Customer Care: +91 9241165807</div>
  <div class="to-label">DELIVER TO:</div>
  <div class="name">${lead.delivery_full_name || lead.name || ''}</div>
  <div class="addr">${lead.delivery_address || ''}<br/>${lead.delivery_city || ''}, ${lead.delivery_state || ''}</div>
  <div class="pin">PIN: ${lead.delivery_pincode || ''}</div>
  <div class="mobile">📞 ${lead.delivery_mobile || lead.mobile1 || ''}</div>
  <div class="meta">Lead ID: ${lead.id}<br/>Paid: ${formatDate(lead.paid_at)}<br/>Payment ID: ${lead.razorpay_payment_id || '-'}</div>
</div>
<script>window.print();</script>
</body></html>`;
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) {
      // Revoke URL after print window has had time to load
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-xl text-gray-700">Loading...</div></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-xl text-red-600">{error}</div></div>;

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }} data-testid="paid-leads-page">
      <header className="bg-white shadow-lg sticky top-0 z-30 border-b-2 border-emerald-500">
        <div className="px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <Link to="/maliha-gupt-dwar-J2ipm3nqQUoUxrmse-rziroFzhFbeWTFeDkX-M6reQY" className="text-blue-600 text-sm font-bold hover:underline" data-testid="back-to-admin-link">← Back to Admin</Link>
            <h1 className="text-xl font-bold text-gray-900" data-testid="paid-leads-title">💰 Paid Customers</h1>
          </div>
          <button onClick={exportCSV} disabled={filtered.length === 0}
            className="px-4 py-2 bg-green-600 text-white text-sm font-bold rounded-lg shadow hover:bg-green-700 disabled:opacity-50" data-testid="export-paid-csv-btn">
            📥 Export CSV
          </button>
        </div>
      </header>

      <div className="px-4 py-4">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <div className="bg-emerald-600 rounded-xl p-4 text-white" data-testid="stat-total-paid">
            <div className="text-3xl font-black">{filtered.length}</div>
            <div className="text-xs font-bold opacity-90">कुल Paid Customers</div>
          </div>
          <div className="bg-amber-600 rounded-xl p-4 text-white" data-testid="stat-total-revenue">
            <div className="text-3xl font-black">₹{totalRevenue.toLocaleString('hi-IN')}</div>
            <div className="text-xs font-bold opacity-90">कुल Revenue</div>
          </div>
          <div className="bg-blue-600 rounded-xl p-4 text-white" data-testid="stat-pending-delivery">
            <div className="text-3xl font-black">{filtered.filter(l => l.delivery_pincode && !l.dispatched_at).length}</div>
            <div className="text-xs font-bold opacity-90">📦 Pending Delivery</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow p-4 mb-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-bold text-gray-600 block mb-1">खोजें (नाम/मोबाइल/शहर/Pincode)</label>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="search..." className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-400 outline-none" data-testid="paid-search-input" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-600 block mb-1">From Date</label>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-400 outline-none" data-testid="date-from-input" />
          </div>
          <div>
            <label className="text-xs font-bold text-gray-600 block mb-1">To Date</label>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-400 outline-none" data-testid="date-to-input" />
          </div>
          {(search || dateFrom || dateTo) && (
            <button onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }}
              className="px-3 py-2 bg-gray-200 text-gray-700 text-sm font-bold rounded-lg hover:bg-gray-300" data-testid="clear-filters-btn">Clear</button>
          )}
        </div>

        {/* Date-grouped paid leads */}
        {groupedByDate.length === 0 && (
          <div className="bg-white rounded-xl p-8 text-center text-gray-500" data-testid="no-paid-message">
            अभी कोई paid customer नहीं है
          </div>
        )}

        {groupedByDate.map(([date, items]) => (
          <div key={date} className="mb-5" data-testid={`date-group-${date}`}>
            <div className="flex items-center gap-3 mb-2 sticky top-[60px] bg-slate-50 py-2 z-10">
              <h2 className="text-lg font-black text-gray-900">📅 {date}</h2>
              <span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-1 rounded-full">{items.length} customers</span>
              <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-1 rounded-full">₹{items.reduce((s, l) => s + (l.payment_amount || 0), 0).toLocaleString('hi-IN')}</span>
            </div>
            <div className="space-y-3">
              {items.map((lead, idx) => (
                <div key={lead.id} className="bg-white rounded-xl shadow border-2 border-emerald-200 p-4" data-testid={`paid-lead-card-${idx}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-3 pb-3 border-b border-gray-100">
                    <div>
                      <div className="font-black text-lg text-gray-900">{lead.delivery_full_name || lead.name}</div>
                      <div className="text-xs text-gray-500">Paid: {formatDate(lead.paid_at)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-emerald-600 text-white text-sm font-black px-3 py-1 rounded-lg">₹{lead.payment_amount || 5500}</span>
                      <button onClick={() => printShippingLabel(lead)}
                        className="px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-bold rounded-lg" data-testid={`print-label-btn-${idx}`}>
                        📦 Print Label
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="text-xs font-bold text-gray-500 uppercase mb-1">📞 Contact</div>
                      <div><b>Mobile:</b> <a href={`tel:+91${lead.delivery_mobile || lead.mobile1}`} className="text-blue-600">{lead.delivery_mobile || lead.mobile1}</a></div>
                      {lead.email && <div><b>Email:</b> {lead.email}</div>}
                      <div className="mt-2 flex gap-2">
                        <a href={`https://wa.me/91${lead.delivery_mobile || lead.mobile1}`} target="_blank" rel="noopener noreferrer"
                          className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded hover:bg-green-200">💬 WhatsApp</a>
                        <a href={`tel:+91${lead.delivery_mobile || lead.mobile1}`}
                          className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded hover:bg-blue-200">☎ Call</a>
                      </div>
                    </div>
                    <div>
                      <div className="text-xs font-bold text-gray-500 uppercase mb-1">📍 Delivery Address</div>
                      {lead.delivery_address ? (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-gray-800">
                          <div>{lead.delivery_address}</div>
                          <div>{lead.delivery_city}, {lead.delivery_state}</div>
                          <div className="font-bold mt-1">PIN: {lead.delivery_pincode}</div>
                        </div>
                      ) : (
                        <div className="text-red-500 text-sm">⚠ Address नहीं भरा गया</div>
                      )}
                    </div>
                  </div>
                  {lead.razorpay_payment_id && (
                    <div className="text-xs text-gray-400 mt-3 pt-2 border-t border-gray-100">Payment ID: {lead.razorpay_payment_id}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PaidLeadsPage;
